import {
  fetcher,
  useClientPortalAPI,
  useClientPortalSubmit,
  useRequest,
} from "@certn/api";
import qs from "qs";
import { downloadBlob } from "./monitoringDownload";
import { useCallback } from "react";
import useSWR from "swr";
import { useSWRConfig } from "swr";

import type {
  EligibleApplicantsResponse,
  MonitoringBulkActionResponse,
  MonitoringEnrollmentDemoResponse,
  MonitoringEnrollmentResponse,
} from "./types";

/** Per-enrollment lifecycle transitions available from the applicant detail page. */
export type EnrollmentLifecycleAction =
  | "pause"
  | "resume"
  | "deactivate"
  | "renew";

export const ELIGIBLE_APPLICANTS_PATH =
  "/api/internal/client-portal/monitoring/eligible-applicants/" as const;
export const DASHBOARD_APPLICANTS_PATH =
  "/api/internal/client-portal/monitoring/applicants/" as const;
export const DASHBOARD_SUMMARY_PATH =
  "/api/internal/client-portal/monitoring/applicants/summary/" as const;
export const BULK_ACTION_PATH =
  "/api/internal/client-portal/monitoring/applicants/bulk-action/" as const;
const ENROLLMENTS_PATH =
  "/api/internal/client-portal/monitoring/enrollments/" as const;
export const MONITORING_APPLICANT_DETAIL_PATH =
  "/api/internal/client-portal/monitoring/applicants/" as const;

/** Backend max page size — fetch all eligible applicants for the enroll flow. */
export const ELIGIBLE_APPLICANTS_ENROLL_QUERY = { page_size: "100" };

export function useEligibleApplicants(query?: Record<string, unknown> | null) {
  const enabled = query !== null;
  return useClientPortalAPI(
    enabled ? ELIGIBLE_APPLICANTS_PATH : null,
    query ?? undefined
  );
}

async function fetchAllEligibleApplicants(
  request: ReturnType<typeof useRequest>
): Promise<EligibleApplicantsResponse> {
  const results: EligibleApplicantsResponse["results"] = [];
  let page = 1;
  let count = 0;

  while (true) {
    const path =
      ELIGIBLE_APPLICANTS_PATH +
      qs.stringify(
        { ...ELIGIBLE_APPLICANTS_ENROLL_QUERY, page: String(page) },
        { addQueryPrefix: true, arrayFormat: "repeat" }
      );
    const data = await request<EligibleApplicantsResponse>(path);
    results.push(...data.results);
    count = data.count;
    if (!data.next) {
      break;
    }
    page += 1;
  }

  return { count, next: null, previous: null, results };
}

/** Eligible checks for one applicant on a specific case (case details enroll banner). */
export function useEligibleApplicantForCase(
  subjectId: string | undefined,
  caseShortId: string | undefined,
  enabled = true
) {
  const shouldFetch = enabled && Boolean(subjectId) && Boolean(caseShortId);
  return useEligibleApplicants(
    shouldFetch
      ? {
          subject_id: subjectId!,
          case_short_id: caseShortId!,
        }
      : null
  );
}

/** Per-subject monitoring detail, including the subject's enrollments. */
export function useMonitoringApplicantDetail(
  subjectId: string | undefined,
  enabled = true
) {
  const shouldFetch = enabled && Boolean(subjectId);
  return useClientPortalAPI(
    shouldFetch ? `${MONITORING_APPLICANT_DETAIL_PATH}${subjectId}/` : null
  );
}

/** Fetches every page of eligible applicants (for enroll flows). */
export function useAllEligibleApplicants(enabled = true) {
  const request = useRequest();
  const result = useSWR(
    enabled ? [ELIGIBLE_APPLICANTS_PATH, "all-pages"] : null,
    () => fetchAllEligibleApplicants(request)
  );
  return result;
}

export function useDashboardApplicants(query?: Record<string, unknown>) {
  return useClientPortalAPI(DASHBOARD_APPLICANTS_PATH, query);
}

export function useDashboardSummary(enabled = true) {
  return useClientPortalAPI(enabled ? DASHBOARD_SUMMARY_PATH : null);
}

export function useBulkMonitoringAction(options?: {
  onSuccess?: (data: MonitoringBulkActionResponse) => void;
  onFailure?: (error: unknown) => void;
}) {
  return useClientPortalSubmit(BULK_ACTION_PATH, {
    showToastOnError: true,
    ...options,
  });
}

export function useRefreshEligibleApplicants() {
  const { mutate } = useSWRConfig();
  return () =>
    mutate((key) => {
      if (typeof key === "string") {
        return key.startsWith(ELIGIBLE_APPLICANTS_PATH);
      }
      return Array.isArray(key) && key[0] === ELIGIBLE_APPLICANTS_PATH;
    });
}

export function useRefreshMonitoringApplicantDetails() {
  const { mutate } = useSWRConfig();
  return () =>
    mutate(
      (key) =>
        typeof key === "string" &&
        key.startsWith(MONITORING_APPLICANT_DETAIL_PATH)
    );
}

export function useRefreshDashboardApplicants() {
  const { mutate } = useSWRConfig();
  return () =>
    mutate(
      (key) =>
        typeof key === "string" &&
        (key.startsWith(DASHBOARD_APPLICANTS_PATH) ||
          key.startsWith(DASHBOARD_SUMMARY_PATH))
    );
}

export function useRefreshMonitoringActivityLog() {
  const { mutate } = useSWRConfig();
  return (subjectId: string) =>
    mutate(
      (key) =>
        typeof key === "string" &&
        key.includes(
          `${MONITORING_APPLICANT_DETAIL_PATH}${subjectId}/activity-log/`
        )
    );
}

/** Revalidate monitoring caches after a lifecycle or enrollment action on one applicant. */
export function useRefreshMonitoringAfterAction(subjectId: string | undefined) {
  const refreshDetail = useRefreshMonitoringApplicantDetails();
  const refreshEligible = useRefreshEligibleApplicants();
  const refreshDashboard = useRefreshDashboardApplicants();
  const refreshActivityLog = useRefreshMonitoringActivityLog();

  return useCallback(() => {
    if (!subjectId) {
      return Promise.resolve();
    }
    return Promise.all([
      refreshDetail(),
      refreshEligible(),
      refreshDashboard(),
      refreshActivityLog(subjectId),
    ]).catch(() => {});
  }, [
    subjectId,
    refreshDetail,
    refreshEligible,
    refreshDashboard,
    refreshActivityLog,
  ]);
}

export function useCreateEnrollments(options?: {
  onSuccess?: (data: MonitoringEnrollmentResponse[]) => void;
  onFailure?: (error: unknown) => void;
}) {
  return useClientPortalSubmit(ENROLLMENTS_PATH, {
    showToastOnError: true,
    ...options,
  });
}

const ENROLLMENTS_BASE_PATH =
  "/api/internal/client-portal/monitoring/enrollments/" as const;

function enrollmentDemoResponsePath(enrollmentId: string) {
  return `${ENROLLMENTS_BASE_PATH}${enrollmentId}/demo-response/` as const;
}

/** Returns a callback that POSTs a lifecycle transition for a single enrollment. */
export function useEnrollmentLifecycleAction() {
  const request = useRequest();
  return useCallback(
    (enrollmentId: string, action: EnrollmentLifecycleAction) =>
      request(`${ENROLLMENTS_BASE_PATH}${enrollmentId}/${action}/`, {
        method: "POST",
      }),
    [request]
  );
}

export function useEnrollmentDemoResponse(
  enrollmentId: string | undefined,
  enabled = true
) {
  const shouldFetch = enabled && Boolean(enrollmentId);
  return useClientPortalAPI(
    shouldFetch
      ? (`/api/internal/client-portal/monitoring/enrollments/${enrollmentId}/demo-response/` as const)
      : null
  );
}

export function useSaveEnrollmentDemoResponse() {
  const request = useRequest();
  return useCallback(
    (enrollmentId: string, demoResponseClaims: Record<string, string>) =>
      request<MonitoringEnrollmentDemoResponse>(
        enrollmentDemoResponsePath(enrollmentId),
        {
          method: "PUT",
          body: JSON.stringify({
            demo_response_claims: demoResponseClaims,
          }),
        }
      ),
    [request]
  );
}

function activityLogPath(subjectId: string) {
  return `${MONITORING_APPLICANT_DETAIL_PATH}${subjectId}/activity-log/` as const;
}

/** Paginated, searchable monitoring activity log for one applicant. */
export function useMonitoringActivityLog(
  subjectId: string | undefined,
  query?: Record<string, string>
) {
  return useClientPortalAPI(
    subjectId ? activityLogPath(subjectId) : null,
    query
  );
}

/** Download the applicant's activity log as a CSV file. */
export async function downloadMonitoringActivityLogCsv(
  subjectId: string,
  query?: Record<string, string | string[]>
): Promise<void> {
  const token = await fetcher.getAccessToken().catch(() => "");
  const path =
    activityLogPath(subjectId) +
    "export/" +
    qs.stringify(query ?? {}, { addQueryPrefix: true, arrayFormat: "repeat" });
  const response = await fetch(new URL(path, fetcher.domain).toString(), {
    credentials: "same-origin",
    headers: { Authorization: `Bearer ${token}`, Accept: "text/csv" },
  });
  if (!response.ok) {
    throw new Error(
      `Activity log CSV export failed with status ${response.status}`
    );
  }
  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("text/csv")) {
    throw new Error(
      `Activity log CSV export returned unexpected content type: ${contentType || "none"}`
    );
  }
  const blob = await response.blob();
  downloadBlob(blob, `monitoring-activity-${subjectId}.csv`);
}
